/**
 * KPI-Cockpit (Finanzen → „Auswertungen").
 *
 * Diese Datei laedt, haelt den Zustand und setzt zusammen — gerechnet wird in
 * `cockpitEngine.ts` (Modell) und `cockpitForecast.ts` (Prognose), gezeichnet
 * in `components/cockpit/*`. Umbauplan: docs/kpi-cockpit-plan.md.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import type { Invoice, UserProfile } from '../types';
import { fetchInvoices } from '../services/invoiceService';
import { istUebungsprojekt } from '../services/uebung/uebungService';
import { useRememberedState } from '../hooks/useRemembered';
import { buildCockpitModel } from '../services/cockpitEngine';
import { buildForecast } from '../services/cockpitForecast';
import type { ProjectRow, ServiceTypeRow } from '../services/cockpitTypes';
import { CockpitGantt } from './cockpit/CockpitGantt';
import { CockpitSettings } from './cockpit/CockpitSettings';
import { CockpitToolbar } from './cockpit/CockpitToolbar';
import { MonthDrilldown } from './cockpit/MonthDrilldown';
import { OverviewTab } from './cockpit/OverviewTab';
import { ProjectDrilldown } from './cockpit/ProjectDrilldown';
import { ProjectsTab } from './cockpit/ProjectsTab';
import { QuotesTab } from './cockpit/QuotesTab';
import { ServiceTypeDrilldown } from './cockpit/ServiceTypeDrilldown';
import { TeamTab } from './cockpit/TeamTab';
import { WarningsModal } from './cockpit/WarningsModal';
import { WorkloadTab } from './cockpit/WorkloadTab';
import { exportCockpitPdf, type PdfContext } from './cockpit/cockpitPdf';
import {
    gueltigerCockpitTab, localIsoToday, makePeriod, periodLabel, periodSlug,
    type CockpitProject, type CockpitTab, type PeriodKind,
} from './cockpit/cockpitUi';
import { offsetsToCents, toWorkloadSettings, useKpiConfig } from './cockpit/kpiConfig';

interface Props { users: UserProfile[]; projects: CockpitProject[]; currentUser: UserProfile | null; }

export const AdminDashboard: React.FC<Props> = ({ users, projects: alleProjekte, currentUser }) => {
    // UEBUNGSPROJEKTE FALLEN IN KEINE AUSWERTUNG (docs/lernzentrum-tutorial-plan.md,
    // 1.11 und 8.2). Gefiltert wird hier und nicht beim Laden, weil dieselbe
    // Liste die Projektverwaltung speist, die Uebungsprojekte sehr wohl zeigt.
    const projects = useMemo(() => alleProjekte.filter((p) => !istUebungsprojekt(p)), [alleProjekte]);

    // Ein Stichtag je Aufbau, aus Ziffern gebaut (zeitzonenfest).
    const today = useMemo(() => localIsoToday(), []);
    const currentYear = Number(today.slice(0, 4));
    const currentMonthIndex = Number(today.slice(5, 7)) - 1;

    const [year, setYear] = useRememberedState<number>('admin.cockpit.year', currentYear);
    const [periodKind, setPeriodKind] = useRememberedState<PeriodKind>('admin.cockpit.periodKind', 'year');
    const [quarter, setQuarter] = useRememberedState<number>('admin.cockpit.quarter', Math.floor(currentMonthIndex / 3) + 1);
    const [month, setMonth] = useRememberedState<number>('admin.cockpit.month', currentMonthIndex + 1);
    const [gemerkterTab, setTab] = useRememberedState<CockpitTab>('admin.cockpit.tab', 'uebersicht');
    // Abgeleitet statt korrigiert: ein Altwert ('api', seit dem Menue-Umbau
    // unter System) faellt auf die Uebersicht, ohne einen zweiten Hook oder
    // Effekt — der naechste Klick ueberschreibt den gemerkten Wert ohnehin.
    const tab = gueltigerCockpitTab(gemerkterTab);
    const [employeeId, setEmployeeId] = useState<string>('all');
    const [hideAmounts, setHideAmounts] = useState(false);

    const [invoices, setInvoices] = useState<Invoice[]>([]);
    const [invoiceError, setInvoiceError] = useState(false);

    const [showSettings, setShowSettings] = useState(false);
    const [showWarnings, setShowWarnings] = useState(false);
    const [drillMonth, setDrillMonth] = useState<number | null>(null);
    // Aufklappungen halten nur den SCHLUESSEL, nicht die Zeile (Befund G12).
    const [drillProjectId, setDrillProjectId] = useState<string | null>(null);
    const [drillServiceTypeKey, setDrillServiceTypeKey] = useState<string | null>(null);
    const [exporting, setExporting] = useState(false);

    // ── Laden ────────────────────────────────────────────────────────────
    // Alle Belege der Firma einmal beim Aufbau. Ohne Positionen: das Cockpit
    // liest ausschliesslich Kopfsummen (revenueNetCents, offener Betrag).
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const list = await fetchInvoices({ withoutLines: true });
                if (!cancelled) { setInvoices(list); setInvoiceError(false); }
            } catch (e) {
                console.error(e);
                if (!cancelled) { setInvoices([]); setInvoiceError(true); }
            }
        })();
        return () => { cancelled = true; };
    }, []);

    // Mandant ueber den ANGEMELDETEN Nutzer, nicht ueber users[0] (Befund 16).
    const companyName = currentUser?.company_name ?? users[0]?.company_name ?? null;

    // `kpi_config` kommt gekoppelt an IHR Jahr (Befunde G2/G3) — siehe kpiConfig.ts.
    const kpi = useKpiConfig(companyName, year, currentUser);
    // `kpi` selbst ist bei jedem Aufbau ein neues Objekt (useKpiConfig gibt ein
    // Literal zurueck) — als Abhaengigkeit erzeugte das bei jedem Rendern eine
    // neue `saveConfig`. Ausgepackt haengt der Hook nur noch an der Funktion,
    // die tatsaechlich gebraucht wird und selbst schon per useCallback stabil
    // ist (Befund I4).
    const { save: saveKpiConfig } = kpi;

    const saveConfig = useCallback(async () => {
        if (await saveKpiConfig()) setShowSettings(false);
        else alert('Einstellungen konnten nicht gespeichert werden.');
    }, [saveKpiConfig]);

    // ── Rechnen ──────────────────────────────────────────────────────────
    const period = useMemo(
        () => makePeriod(year, periodKind, periodKind === 'quarter' ? quarter : month),
        [year, periodKind, quarter, month],
    );
    const workloadSettings = useMemo(() => toWorkloadSettings(kpi.workload, projects), [kpi.workload, projects]);
    const offsetsCents = useMemo(() => offsetsToCents(kpi.offsets), [kpi.offsets]);

    const forecast = useMemo(
        () => buildForecast({ projects, users, year, today, workload: workloadSettings }),
        [projects, users, year, today, workloadSettings],
    );

    const model = useMemo(
        () => buildCockpitModel({ projects, users, invoices, period, employeeId, offsetsCents, today, forecast }),
        [projects, users, invoices, period, employeeId, offsetsCents, today, forecast],
    );

    // ── Aus dem Bestand abgeleitet (steht nicht im Modell) ───────────────
    const projectNumbers = useMemo(() => {
        const map: Record<string, string> = {};
        projects.forEach((p) => { map[p.id] = p.project_number ?? '—'; });
        return map;
    }, [projects]);

    const assignmentByService = useMemo(() => {
        const map: Record<string, string> = {};
        // Dieselbe Normalisierung wie in der Engine: leer heisst `additional`,
        // sonst traefe die Leistungsart-Aufklappung ihre eigenen Eintraege nicht.
        projects.forEach((p) => (p.project_services ?? []).forEach((s) => {
            map[String(s.id)] = String(s.assignment ?? '').trim() || 'additional';
        }));
        return map;
    }, [projects]);

    const years = useMemo(() => {
        let earliest = currentYear;
        projects.forEach((p) => {
            const iso = p.start_date ?? p.data?.metadata?.globalData?.startDate ?? '';
            const value = Number(String(iso).slice(0, 4));
            if (Number.isFinite(value) && value >= 2000 && value < earliest) earliest = value;
        });
        const list: number[] = [];
        for (let y = currentYear; y >= earliest; y--) list.push(y);
        if (!list.includes(year)) list.push(year);
        return list.sort((a, b) => b - a);
    }, [projects, currentYear, year]);

    const periodText = periodLabel(period);
    const todayMonth = year === currentYear ? currentMonthIndex : null;

    const pdfContext = useMemo<PdfContext>(() => ({
        companyName: companyName ?? 'GefAS mbH',
        filterLabel: employeeId === 'all'
            ? 'Gesamtes Team'
            : model.employees.find((e) => e.id === employeeId)?.name ?? 'Auswahl',
        periodText,
        periodSlug: periodSlug(period),
        hideAmounts,
    }), [companyName, employeeId, model.employees, periodText, period, hideAmounts]);

    const handlePeriod = useCallback((kind: PeriodKind, value: number) => {
        // Jede Art merkt sich ihren eigenen Wert: der Wechsel Monat → Quartal
        // darf aus „Juli" kein „Q7" machen.
        if (kind === periodKind) {
            if (kind === 'quarter') setQuarter(value);
            else if (kind === 'month') setMonth(value);
        }
        setPeriodKind(kind);
    }, [periodKind, setPeriodKind, setQuarter, setMonth]);

    const exportPdf = useCallback(async () => {
        setExporting(true);
        try {
            await exportCockpitPdf(model, pdfContext);
        } catch (e) {
            console.error(e);
            alert('PDF-Export fehlgeschlagen.');
        } finally {
            setExporting(false);
        }
    }, [model, pdfContext]);

    const openProject = useCallback((project: ProjectRow) => {
        setDrillServiceTypeKey(null);
        setDrillProjectId(project.id);
    }, []);
    const openServiceType = useCallback((type: ServiceTypeRow) => setDrillServiceTypeKey(type.key), []);

    // Die Zeile kommt bei JEDEM Aufbau frisch aus dem Modell. Frueher lag sie
    // selbst im Zustand — nach einem Wechsel von Zeitraum oder Filter zeigte der
    // Dialog den Stand von vorhin, waehrend die Kacheln dahinter schon neu
    // rechneten (Befund G12). Ohne Zeile im Modell ist die Aufklappung zu.
    const drillProject = drillProjectId ? model.projects.find((p) => p.id === drillProjectId) ?? null : null;
    const drillServiceType = drillServiceTypeKey
        ? model.serviceTypes.find((t) => t.key === drillServiceTypeKey) ?? null
        : null;

    const drillProjectSource = drillProject ? projects.find((p) => p.id === drillProject.id) : undefined;

    return (
        <div className="space-y-5 pb-20">
            <CockpitToolbar
                tab={tab}
                onTab={setTab}
                year={year}
                years={years}
                onYear={setYear}
                periodKind={periodKind}
                periodValue={periodKind === 'quarter' ? quarter : month}
                onPeriod={handlePeriod}
                employeeId={employeeId}
                users={users}
                onEmployee={setEmployeeId}
                warningCount={model.warnings.length}
                onWarnings={() => setShowWarnings(true)}
                hideAmounts={hideAmounts}
                onToggleAmounts={() => setHideAmounts((v) => !v)}
                onExportPdf={exportPdf}
                exporting={exporting}
                onSettings={() => setShowSettings(true)}
            />

            {invoiceError && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-xl border border-amber-200 bg-amber-50 text-[11px] text-amber-700">
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                    Belege konnten nicht geladen werden — „Abgerechnet" und „Offene Forderungen" bleiben leer.
                </div>
            )}

            {kpi.failed && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-xl border border-amber-200 bg-amber-50 text-[11px] text-amber-700">
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                    Cockpit-Einstellungen konnten nicht geladen werden — externer Umsatz und Zeitansätze rechnen
                    mit Vorgabewerten, Speichern ist gesperrt.
                </div>
            )}

            {tab === 'uebersicht' && (
                <OverviewTab
                    model={model}
                    hideAmounts={hideAmounts}
                    periodText={periodText}
                    todayMonth={todayMonth}
                    onSelectMonth={setDrillMonth}
                    onSelectProject={openProject}
                    onSelectServiceType={openServiceType}
                />
            )}

            {tab === 'auslastung' && (
                <WorkloadTab
                    model={model}
                    hideAmounts={hideAmounts}
                    todayMonth={todayMonth}
                    onSelectMonth={setDrillMonth}
                />
            )}

            {tab === 'team' && (
                <TeamTab
                    model={model}
                    hideAmounts={hideAmounts}
                    todayMonth={todayMonth}
                    employeeId={employeeId}
                    onSelectEmployee={setEmployeeId}
                />
            )}

            {tab === 'projekte' && (
                <ProjectsTab
                    model={model}
                    numbers={projectNumbers}
                    hideAmounts={hideAmounts}
                    onSelect={openProject}
                />
            )}

            {tab === 'gantt' && (
                <CockpitGantt
                    model={model}
                    numbers={projectNumbers}
                    pdfContext={pdfContext}
                    onSelect={openProject}
                />
            )}

            {tab === 'angebote' && <QuotesTab periodKind={periodKind} />}

            {drillMonth !== null && (
                <MonthDrilldown
                    model={model}
                    forecast={forecast}
                    monthIndex={drillMonth}
                    hideAmounts={hideAmounts}
                    pdfContext={pdfContext}
                    onMonth={setDrillMonth}
                    onClose={() => setDrillMonth(null)}
                />
            )}

            {drillProject && (
                <ProjectDrilldown
                    project={drillProject}
                    projectNumber={projectNumbers[drillProject.id] ?? '—'}
                    services={drillProjectSource?.project_services ?? []}
                    performed={model.performedDetails.filter((d) => d.projectId === drillProject.id)}
                    hideAmounts={hideAmounts}
                    periodText={periodText}
                    onClose={() => setDrillProjectId(null)}
                />
            )}

            {drillServiceType && (
                <ServiceTypeDrilldown
                    serviceType={drillServiceType}
                    model={model}
                    assignmentByService={assignmentByService}
                    hideAmounts={hideAmounts}
                    periodText={periodText}
                    onSelectProject={openProject}
                    onClose={() => setDrillServiceTypeKey(null)}
                />
            )}

            {showWarnings && (
                <WarningsModal warnings={model.warnings} onClose={() => setShowWarnings(false)} />
            )}

            {showSettings && (
                <CockpitSettings
                    year={year}
                    offsets={kpi.offsets}
                    onOffsets={kpi.setOffsets}
                    workload={kpi.workload}
                    onWorkload={kpi.setWorkload}
                    lastEdit={kpi.lastEdit}
                    saving={kpi.saving}
                    saveBlocked={kpi.saveBlocked}
                    onSave={saveConfig}
                    onClose={() => setShowSettings(false)}
                />
            )}
        </div>
    );
};
